/**
 * BETTER CALL ANDREW — SITE CONFIG
 * ---------------------------------
 * Every piece of editable content lives here. Components only read from this file.
 * Empty strings ("") render as a calm "on request" field — never as loud placeholder text.
 * Image paths point to /public. Drop your files there and update the path.
 */

export const identity = {
  name: "Andrew Nithin",
  monogram: "AN",
  brand: "Better Call Andrew",
  location: "Mumbai area",
};

export const assets = {
  logo: "/logo.webp", // Better Call Andrew wordmark (used as given)
  heroPhoto: "/andrew-hero.webp", // pointing cutout, used exactly as uploaded
  dividerIcon: "/micpen.webp", // mic + pen icon for section dividers
  ogImage: "/og-image.jpg",
};

export const links = {
  linkedin: "https://www.linkedin.com/in/", // add your handle
  github: "https://github.com/", // add your handle
  x: "https://x.com/", // add your handle
  email: "", // shown in the Contact section (mailto). Voicemail recipient is set in backend/.env OWNER_EMAIL
};

export const hero = {
  headline: "I build data and agentic systems that turn data into decisions.",
  subhead:
    "From pipelines to production agents, I design intelligent systems that work while everyone else sleeps.",
  tagline: "By day, data pipelines. By night, agents that never sleep.",
  primaryCta: "Here's my opening statement.",
  secondaryCta: "Connect on LinkedIn.",
  transcriptLink: "Read the transcript",
  transcript:
    "Hi I am Andrew Nithin. By day I'm a data engineer. By night I build agents and AI. I design pipelines that deliver clean evidence on time and agents that keep working the case while everyone else sleeps. Voice is how I think, how I build, and how I explain. This entire site was built by speaking. If your data needs a closing argument that runs in production, better call Andrew.",
  openingStatementAudio: "", // optional mp3/wav in /public. Empty = browser voice reads the transcript.
};

export const voiceNav = {
  prompt: "Try saying, \"Show me your projects.\"",
  hints: ["show cases", "open case 2", "next case", "go to contact", "open chat", "what can I say?"],
};

export const about = {
  headline: "Lawyers use words. I use data and agents.",
  day: {
    title: "Day shift — Data Engineer",
    points: ["Data pipelines, end to end", "Python and PySpark", "Azure Databricks"],
    line: "Courtroom procedure for numbers — clean evidence, presented on time.",
  },
  night: {
    title: "Night shift — Agent AI Developer",
    points: ["LangGraph and LangChain agents", "MCP and A2A tool servers", "AgentBricks and Semantic Kernel"],
    line: "The man without fear of a 3 a.m. production alert.",
  },
};

export const cases = {
  headline: "Four cases, four verdicts.",
  note: "All four cases are personal projects.",
  items: [
    {
      id: 1,
      title: "The Power of Attorney",
      hook: "One server. Every agent. Two hundred and forty tools.",
      badge: "Case 01",
      chips: ["MCP", "A2A", "FastMCP", "FastAPI"],
      problem:
        "Every new agent meant rebuilding the same integrations again. Teams and individuals needed one place where any agent could pick up the tools it needs.",
      move:
        "A single MCP and A2A server with 240 tools across GitHub, Jira, Confluence, Databricks, Azure, Microsoft Fabric and Azure Data Factory. Any agent reuses the same tools, so nobody rebuilds integrations for every new agent. It can serve individuals and teams.",
      safeguards:
        "Permissions, approvals, rate limits and full logging. Authentication uses static tokens today, and OAuth2 is the next step.",
      outcome: "", // editable
    },
    {
      id: 2,
      title: "The Joint Defense",
      hook: "A Scrum team where every seat is an agent.",
      badge: "Case 02",
      chips: ["LangGraph", "Databricks", "Agents"],
      problem:
        "The software delivery cycle has many hand-offs. Each one is a chance for context to be lost between planning, analysis, building and testing.",
      move:
        "A Scrum style team of agents for the software delivery cycle, made of a Scrum master agent, a business analyst agent, a developer agent and a tester agent, built with LangGraph and a Databricks agent framework.",
      safeguards:
        "Two modes: autonomous mode, and human in the loop mode, where a human expert approves every step.",
      outcome: "", // editable
    },
    {
      id: 3,
      title: "The Burden of Proof",
      hook: "Answers that can show their sources.",
      badge: "Case 03",
      chips: ["Agentic RAG", "Hybrid search", "Reranker"],
      problem:
        "Legal style documents punish guesswork. A useful answer has to come from the text, and it has to be able to point back to it.",
      move:
        "An agentic RAG system on legal style documents, with hybrid search, a reranker, metadata filtering, an agent that decides its retrieval steps, and answers grounded in what was retrieved.",
      safeguards:
        "Answers are grounded in retrieved passages. Metadata filtering narrows the evidence before the agent reasons over it.",
      outcome: "", // editable — measured on a small test set
      outcomeNote: "Measured on a small test set.",
    },
    {
      id: 4,
      title: "The Oral Argument",
      hook: "A voice that takes the customer's call.",
      badge: "Case 04",
      chips: ["Voice", "STT", "LLM", "TTS"],
      problem:
        "A restaurant's customers ask the same questions at every hour. Someone has to answer, clearly and quickly, even when the floor is busy.",
      move:
        "A voice customer executive agent that handles a restaurant's customer queries end to end, from speech in to speech out.",
      safeguards:
        "The agent stays within the restaurant's own information and hands off anything it cannot answer.",
      outcome: "", // editable
      stack: { stt: "", llm: "", tts: "", responseTime: "" }, // editable
    },
  ],
};

export const owned = {
  headline: "Things I owned, end to end.",
  items: [
    {
      title: "BIONIC Fabric workstream",
      owned: "Led the BIONIC Fabric workstream, including data estate modernization.",
      call: "", // editable
      hard: "", // editable
      result: "", // editable
    },
    {
      title: "240 tool MCP and A2A server",
      owned: "A single MCP and A2A server with 240 tools that any agent can reuse.",
      call: "One shared server that every agent reuses, instead of integrations rebuilt per agent.",
      hard: "", // editable
      result: "Honest next step: moving from static tokens to OAuth2.",
    },
    {
      title: "SDLC agent suite",
      owned: "The SDLC agent suite, including the autonomous Scrum master agent and the business analyst agent.",
      call: "Offering both autonomous and human approved modes.",
      hard: "", // editable
      result: "", // editable
    },
    {
      title: "Framework map",
      isMap: true,
      groups: [
        { label: "Orchestration", items: ["AgentBricks on Databricks", "LangChain", "LangGraph", "Microsoft agent framework"] },
        { label: "Tools and protocols", items: ["MCP", "FastMCP", "A2A"] },
        { label: "Serving", items: ["FastAPI"] },
      ],
    },
  ],
  labels: { owned: "What I owned", call: "The call I made", hard: "What was hard", result: "What came out of it" },
};

export const tools = {
  headline: "Tools of the trade.",
  groups: [
    { label: "Data", items: ["Python", "PySpark", "Azure Databricks"] },
    { label: "Agents", items: ["LangChain", "LangGraph", "Semantic Kernel", "AgentBricks", "MCP", "A2A"] },
    { label: "Cloud and APIs", items: ["Azure AI Foundry", "FastAPI"] },
  ],
};

export const languages = {
  headline: "Three Languages, One Defence",
  subline: "I can argue my case in three languages.",
  items: [
    { code: "en", lang: "en-IN", label: "English", native: "Hello", greeting: "Hello, I'm Andrew. Let's talk.", audio: "" },
    { code: "hi", lang: "hi-IN", label: "Hindi", native: "नमस्ते", greeting: "नमस्ते, मैं एंड्रयू हूँ। चलिए बात करते हैं।", audio: "" },
    { code: "ta", lang: "ta-IN", label: "Tamil", native: "வணக்கம்", greeting: "வணக்கம், நான் ஆண்ட்ரூ. பேசலாம்.", audio: "" },
  ],
};

export const credentials = {
  headline: "Credentials on file.",
  line: "Credentials open doors. Work wins cases.",
  items: [
    { issuer: "Microsoft Azure", name: "" }, // editable certification name
    { issuer: "Databricks", name: "" }, // editable certification name
  ],
};

export const terms = {
  preline: "Did you know you have rights?",
  attribution: "— after a certain Albuquerque attorney",
  headline: "Terms of engagement.",
  fields: [
    { label: "Location", value: "Mumbai area, willing to relocate to Bangalore" },
    { label: "Interested in", value: "Full time" },
    { label: "Work mode", value: "" }, // editable
    { label: "Available from", value: "" }, // editable
    { label: "Notice period", value: "" }, // editable
    { label: "Contact preference", value: "Email, LinkedIn or voice note" },
  ],
};

export const contact = {
  headline: "You don't need an AI developer. You need an intelligent one.",
  callout: "Better call Andrew.",
  subline: "Data you can trust. Agents you can govern.",
  voicemailCta: "Leave a voicemail",
  successToast: "S'all good, man. Message received.",
};

export const footer = {
  built: "Built entirely by voice with Wispr Flow and Emergent.",
  joke: "No lawyers were harmed in the making of this portfolio.",
  signoff: "S'all good, man.",
};

export const notFound = {
  headline: "Objection! This page doesn't exist.",
  cta: "Back to the courtroom",
};

export const chat = {
  title: "Ask Andrew",
  subtitle: "Answers only from what's on this site.",
  chips: ["What's your best case?", "Are you available to start?", "What's in your stack?"],
  placeholder: "Ask about my work, stack or availability",
};

export const EMPTY_FIELD = "Available on request";
